import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Result,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  MoreOutlined,
  RollbackOutlined,
  ShopOutlined,
  StopOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFirestore } from "react-redux-firebase";
import { useNavigate, useParams } from "react-router-dom";

import {
  ActivityTimeline,
  DetailLayout,
  KeyValueList,
  Num,
  PageHeader,
  SectionCard,
  StickyActionBar,
} from "components/shared-components/purchase";
import OrderDrawer from "components/shared-components/Orders/OrderDrawer";
import { CURRENCY } from "constants/DeliveryConstants";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import { FIRESTORE_QUOTES_TABLE } from "constants/FirestoreConstant";
import { formatPrice } from "functions/formatters";
import { PERMISSIONS } from "functions/permissions";
import {
  QUOTE_DISPLAY_STATUS,
  QUOTE_SEND_STATE,
  QUOTE_STATUS,
  applyLinePriceToProduct,
  calculateQuoteTotals,
  getQuoteDisplayStatus,
  getQuoteSendState,
  getQuoteValidUntil,
  isQuoteOpen,
  resetLinePrice,
  setLineDiscountPercentage,
  setLinePrice,
} from "functions/quotes";
import { useAuth } from "data/hooks/useAuth";
import { usePermissions } from "data/hooks/usePermissions";
import { useQuote } from "data/hooks/useQuotes";
import urls from "urls";
import SendQuoteModal from "../SendQuoteModal";
import ConvertQuoteModal from "../ConvertQuoteModal";
import { QUOTE_STATUS_TAG, QuoteSendTag, customerLabel } from "../shared";
import { notifyConverted } from "../notifyConverted";

const nowSeconds = () => Math.floor(Date.now() / 1000);

const formatAddress = (address) => {
  if (!address) return null;
  const name = [address.firstName, address.lastName].filter(Boolean).join(" ");
  const street = [address.street, `${address.houseNumber || ""}${address.addition ? ` ${address.addition}` : ""}`]
    .filter(Boolean)
    .join(" ");
  return [address.companyName, name, street, [address.zipCode, address.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join("\n");
};

/**
 * Eén offerte (BMX-58).
 *
 * Hoofdkolom: de regels (in bewerkmodus met aantal, eigen prijs of korting per product), de
 * opmerkingen en de geschiedenis. Zijkolom: klant, offertegegevens en bedragen.
 *
 * Bewerken hier is voor prijzen en aantallen. Producten toevoegen gaat via "Bewerken in webshop":
 * die zet de offerte in het mandje, met de prijzen, voorraad en verzendkosten van de webshop. Elke
 * opgeslagen wijziging is een nieuwe versie met een nieuwe geldigheid van 30 dagen; de klant
 * heeft dan nog de vorige, tot je hem opnieuw verstuurt.
 */
const QuoteDetail = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [messageApi, contextHolder] = message.useMessage();
  const { uid, profile } = useAuth();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.QUOTES_MANAGE);
  const { quote, loading, error } = useQuote(quoteId);

  const [draft, setDraft] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);

  const orderDoc = useSelector((state) =>
    quote?.orderDocumentId ? state.firestoreReducer.data.orders?.[quote.orderDocumentId] : null,
  );
  const convertedOrder = useMemo(
    () => (orderDoc ? { id: quote.orderDocumentId, ...orderDoc } : null),
    [orderDoc, quote?.orderDocumentId],
  );

  const editing = !!draft;
  const lines = editing ? draft.products : quote?.products || [];

  const totals = useMemo(() => {
    if (!quote) return null;
    if (editing) return calculateQuoteTotals(quote, draft.products, draft.deliveryCost);
    return {
      linesTotal: (quote.products || []).reduce((sum, line) => sum + line.paymentPrice * line.quantity, 0),
      campaignDiscount: quote.campaignDiscount || null,
      totalPrice: quote.totalPrice,
      vat: quote.vat ?? null,
      deliveryCost: quote.delivery?.cost ?? null,
    };
  }, [quote, draft, editing]);

  if (loading) {
    return (
      <div>
        <PageHeader title={t("quote.quote")} crumbLabel={null} />
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <Result
        status={error ? "error" : "404"}
        title={error ? t("quote.load_failed") : t("quote.not_found")}
        extra={
          <Button type="primary" onClick={() => navigate(urls.quotesList)}>
            {t("back")}
          </Button>
        }
      />
    );
  }

  const displayStatus = getQuoteDisplayStatus(quote);
  const open = isQuoteOpen(quote);
  const sendState = getQuoteSendState(quote);
  const version = quote.version || 1;
  const formatDate = (seconds) => (seconds ? dayjs.unix(seconds).format(DATE_FORMAT_DD_MM_YYYY) : "-");
  const formatDateTime = (seconds) => (seconds ? dayjs.unix(seconds).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-");
  const money = (value) => CURRENCY + formatPrice(value || 0);
  const editor = {
    uid: uid || null,
    email: profile?.email || null,
    name: [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || profile?.email || null,
  };

  // --- bewerken -------------------------------------------------------------

  const startEditing = () =>
    setDraft({
      products: (quote.products || []).map((line) => ({ ...line })),
      deliveryCost: quote.delivery?.cost ?? null,
      orderNotes: quote.orderNotes || "",
      validUntil: quote.validUntil || null,
      validUntilTouched: false,
      baseVersion: version,
    });

  const updateLine = (index, change) =>
    setDraft((current) => ({ ...current, products: current.products.map((l, i) => (i === index ? change(l) : l)) }));
  const updateProductPrice = (index, change) =>
    setDraft((current) => ({ ...current, products: applyLinePriceToProduct(current.products, index, change) }));
  const removeLine = (index) =>
    setDraft((current) => ({ ...current, products: current.products.filter((_, i) => i !== index) }));

  const isDirty =
    editing &&
    (JSON.stringify(draft.products) !== JSON.stringify(quote.products || []) ||
      draft.deliveryCost !== (quote.delivery?.cost ?? null) ||
      (draft.orderNotes || "") !== (quote.orderNotes || "") ||
      draft.validUntilTouched);

  const save = async () => {
    if (!draft.products.length) {
      messageApi.error(t("quote.edit.no_lines"));
      return;
    }
    setIsSaving(true);
    const ref = firestore.collection(FIRESTORE_QUOTES_TABLE).doc(quote.id);
    try {
      await firestore.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(ref);
        const current = snapshot.data();
        if (!current || !isQuoteOpen(current)) throw new Error(t("quote.edit.not_open"));
        // Iemand anders (of de webshop) heeft hem intussen aangepast: niet stilletjes overschrijven
        if ((current.version || 1) !== draft.baseVersion) throw new Error(t("quote.edit.conflict"));
        const now = nowSeconds();
        const result = calculateQuoteTotals(current, draft.products, draft.deliveryCost);
        transaction.update(ref, {
          products: draft.products,
          containsUnavailableProducts: draft.products.some((line) => !!line.unavailableLine),
          totalPrice: result.totalPrice,
          vat: result.vat,
          campaignDiscount: result.campaignDiscount,
          "delivery.cost": result.deliveryCost,
          orderNotes: draft.orderNotes?.trim() ? draft.orderNotes.trim() : null,
          validUntil: draft.validUntilTouched && draft.validUntil ? draft.validUntil : getQuoteValidUntil(now),
          version: draft.baseVersion + 1,
          updatedAt: now,
          updatedBy: editor,
        });
      });
      setDraft(null);
      messageApi.success(t("quote.edit.saved", { version: draft.baseVersion + 1 }));
    } catch (err) {
      console.error("Offerte opslaan mislukt:", err);
      messageApi.error(err?.message || t("quote.edit.save_failed"));
    } finally {
      setIsSaving(false);
    }
  };

  const cancelQuote = () =>
    Modal.confirm({
      title: t("quote.cancel.title", { number: quote.quoteNumber }),
      content: t("quote.cancel.content"),
      okText: t("quote.cancel.ok"),
      okButtonProps: { danger: true },
      cancelText: t("back"),
      onOk: async () => {
        const ref = firestore.collection(FIRESTORE_QUOTES_TABLE).doc(quote.id);
        try {
          await firestore.runTransaction(async (transaction) => {
            const snapshot = await transaction.get(ref);
            const current = snapshot.data();
            if (!current || !isQuoteOpen(current)) throw new Error(t("quote.edit.not_open"));
            const now = nowSeconds();
            transaction.update(ref, {
              status: {
                current: QUOTE_STATUS.CANCELLED,
                history: [
                  ...(current.status?.history || []),
                  {
                    from: current.status?.current || QUOTE_STATUS.OPEN,
                    to: QUOTE_STATUS.CANCELLED,
                    at: now,
                    by: editor.email,
                  },
                ],
              },
              updatedAt: now,
              updatedBy: editor,
            });
          });
          messageApi.success(t("quote.cancel.done"));
        } catch (err) {
          console.error("Offerte annuleren mislukt:", err);
          messageApi.error(err?.message || t("quote.cancel.failed"));
        }
      },
    });

  // --- regels ---------------------------------------------------------------

  const lineTags = (line) => (
    <>
      {line.preOrderLine ? (
        <Tag color="blue">
          {line.preOrder?.availableAt
            ? t("quote.kind.pre_order_from", { date: line.preOrder.availableAt })
            : t("quote.kind.pre_order")}
        </Tag>
      ) : null}
      {line.unavailableLine ? <Tag color="gold">{t("unavailable_item")}</Tag> : null}
      {line.manualPrice ? (
        <Tooltip title={t("quote.manual_price_tooltip", { price: money(line.listPrice) })}>
          <Tag color="purple">{t("quote.manual_price")}</Tag>
        </Tooltip>
      ) : null}
    </>
  );

  const lineColumns = [
    {
      title: t("product"),
      dataIndex: "name",
      render: (value, line) => (
        <>
          <div>{value}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", marginTop: 2 }}>
            {line.code ? (
              <Num muted size={12}>
                {line.code}
              </Num>
            ) : null}
            {lineTags(line)}
          </div>
        </>
      ),
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      width: editing ? 110 : 80,
      align: "right",
      render: (value, line, index) =>
        editing ? (
          <InputNumber
            size="small"
            min={1}
            precision={0}
            value={value}
            style={{ width: 90 }}
            onChange={(next) => next && updateLine(index, (l) => ({ ...l, quantity: next }))}
          />
        ) : (
          <Num align="right">{value}</Num>
        ),
    },
    {
      title: (
        <Tooltip title={t("quote.list_price_tooltip")}>
          <span>{t("quote.list_price")}</span>
        </Tooltip>
      ),
      dataIndex: "originalPrice",
      width: 110,
      align: "right",
      render: (value) => (
        <Num align="right" muted>
          {money(value)}
        </Num>
      ),
    },
    {
      title: t("quote.discount_percentage"),
      dataIndex: "discountPercentage",
      width: editing ? 110 : 90,
      align: "right",
      render: (value, line, index) =>
        editing ? (
          <InputNumber
            size="small"
            min={0}
            max={100}
            precision={2}
            value={value || 0}
            style={{ width: 90 }}
            onChange={(next) => next !== null && updateProductPrice(index, (l) => setLineDiscountPercentage(l, next))}
          />
        ) : (
          <Num align="right" muted={!value}>
            {value ? `${formatPrice(value)}%` : "-"}
          </Num>
        ),
    },
    {
      title: t("quote.unit_price"),
      dataIndex: "paymentPrice",
      width: editing ? 130 : 110,
      align: "right",
      render: (value, line, index) =>
        editing ? (
          <InputNumber
            size="small"
            min={0}
            precision={2}
            prefix="€"
            value={value}
            style={{ width: 110 }}
            onChange={(next) => next !== null && updateProductPrice(index, (l) => setLinePrice(l, next))}
          />
        ) : (
          <Num align="right">{money(value)}</Num>
        ),
    },
    {
      title: t("quote.line_total"),
      key: "total",
      width: 120,
      align: "right",
      render: (_, line) => <Num align="right">{money(line.paymentPrice * line.quantity)}</Num>,
    },
    editing
      ? {
          title: "",
          key: "edit",
          width: 80,
          render: (_, line, index) => (
            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
              {line.manualPrice ? (
                <Tooltip title={t("quote.reset_price")}>
                  <Button
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={() => updateProductPrice(index, resetLinePrice)}
                  />
                </Tooltip>
              ) : null}
              <Tooltip title={t("delete")}>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeLine(index)} />
              </Tooltip>
            </div>
          ),
        }
      : null,
  ].filter(Boolean);

  // --- zijkolom -------------------------------------------------------------

  const customer = quote.customer || {};
  const deliveryAddress = formatAddress(customer.address?.delivery);
  const invoiceAddress = formatAddress(customer.address?.invoice) || deliveryAddress;

  const customerItems = [
    { label: t("customer.key"), value: customerLabel(customer), wrap: true },
    {
      label: t("quote.contact"),
      value: [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "-",
      wrap: true,
    },
    { label: t("quote.email"), value: customer.email || "-", wrap: true },
    customer.phoneNumber ? { label: t("quote.phone"), value: customer.phoneNumber } : null,
    {
      label: t("quote.delivery_address"),
      value: <span style={{ whiteSpace: "pre-line" }}>{deliveryAddress || "-"}</span>,
      wrap: true,
    },
    {
      label: t("quote.invoice_address"),
      value: <span style={{ whiteSpace: "pre-line" }}>{invoiceAddress || "-"}</span>,
      wrap: true,
    },
    customer.vatNumber ? { label: t("quote.vat_number"), value: customer.vatNumber } : null,
    quote.isNewCustomer
      ? { label: t("quote.new_customer"), value: t("quote.new_customer_explained"), wrap: true }
      : null,
  ];

  const quoteItems = [
    {
      label: t("status"),
      value: <Tag color={QUOTE_STATUS_TAG[displayStatus]?.color}>{t(QUOTE_STATUS_TAG[displayStatus]?.key)}</Tag>,
    },
    { label: t("quote.version"), value: <Num>{version}</Num> },
    {
      label: t("quote.valid_until"),
      stacked: editing,
      value: editing ? (
        <DatePicker
          locale={locale}
          format={DATE_FORMAT_DD_MM_YYYY}
          allowClear={false}
          style={{ width: "100%" }}
          value={draft.validUntil ? dayjs.unix(draft.validUntil) : null}
          onChange={(value) =>
            value &&
            setDraft((current) => ({ ...current, validUntil: value.endOf("day").unix(), validUntilTouched: true }))
          }
        />
      ) : (
        <span style={displayStatus === QUOTE_DISPLAY_STATUS.EXPIRED ? { color: "#d4380d" } : undefined}>
          {formatDate(quote.validUntil)}
        </span>
      ),
    },
    open ? { label: t("quote.sent"), value: <QuoteSendTag quote={quote} /> } : null,
    { label: t("quote.created_at"), value: formatDateTime(quote.createdAt) },
    { label: t("quote.salesperson"), value: quote.salesPersonName || quote.createdBy?.name || "-", wrap: true },
    quote.updatedAt && version > 1
      ? {
          label: t("quote.updated_at"),
          value: `${formatDateTime(quote.updatedAt)}${quote.updatedBy?.name ? ` · ${quote.updatedBy.name}` : ""}`,
          wrap: true,
        }
      : null,
    quote.delivery?.preferredDate ? { label: t("quote.preferred_date"), value: quote.delivery.preferredDate } : null,
    { label: t("quote.language"), value: (quote.language || "nl").toUpperCase() },
  ];

  const amountItems = [
    { label: t("quote.lines_total"), value: <Num>{money(totals.linesTotal)}</Num> },
    totals.campaignDiscount?.amount
      ? {
          label: totals.campaignDiscount.percentage
            ? `${t("quote.campaign_discount")} (${totals.campaignDiscount.percentage}%)`
            : t("quote.campaign_discount"),
          value: <Num>− {money(totals.campaignDiscount.amount)}</Num>,
        }
      : null,
    quote.discount?.totalDiscount
      ? { label: t("discount"), value: <Num>− {money(quote.discount.totalDiscount)}</Num> }
      : null,
    {
      label: t("quote.delivery_cost"),
      stacked: editing,
      value: editing ? (
        <InputNumber
          min={0}
          precision={2}
          prefix="€"
          placeholder={t("quote.delivery_afterwards")}
          style={{ width: "100%" }}
          value={draft.deliveryCost}
          onChange={(next) => setDraft((current) => ({ ...current, deliveryCost: next ?? null }))}
        />
      ) : totals.deliveryCost === null ? (
        <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>{t("quote.delivery_afterwards")}</span>
      ) : (
        <Num>{money(totals.deliveryCost)}</Num>
      ),
    },
    { label: t("quote.total_ex_vat"), value: <Num strong>{money(totals.totalPrice)}</Num> },
    totals.vat !== null ? { label: t("vat"), value: <Num>{money(totals.vat)}</Num> } : null,
    totals.vat !== null
      ? { label: t("quote.total_inc_vat"), value: <Num strong>{money(totals.totalPrice + totals.vat)}</Num> }
      : null,
  ];

  // --- geschiedenis ---------------------------------------------------------

  const history = [
    {
      key: "created",
      at: quote.createdAt || 0,
      text: t("quote.history.created", { name: quote.createdBy?.name || quote.salesPersonName || "-" }),
    },
    ...(quote.sends || []).map((send, index) => ({
      key: `send-${index}`,
      at: send.at,
      text: t("quote.history.sent", { version: send.version || 1, email: send.to, name: send.by || "-" }),
    })),
    quote.lastSendFailedAt
      ? {
          key: "send-failed",
          at: quote.lastSendFailedAt,
          text: t("quote.history.send_failed", { error: quote.lastSendError || "-" }),
        }
      : null,
    version > 1 && quote.updatedAt
      ? {
          key: "updated",
          at: quote.updatedAt,
          text: t("quote.history.updated", { version, name: quote.updatedBy?.name || "-" }),
        }
      : null,
    ...(quote.status?.history || [])
      .filter((entry) => entry.to !== QUOTE_STATUS.OPEN)
      .map((entry, index) => ({
        key: `status-${index}`,
        at: entry.at,
        text: t(`quote.history.status_${(entry.to || "").toLowerCase()}`, { name: entry.by || "-" }),
      })),
  ]
    .filter(Boolean)
    .sort((a, b) => (b.at || 0) - (a.at || 0))
    .map((item) => ({ key: item.key, text: item.text, meta: formatDateTime(item.at) }));

  // --- kop ------------------------------------------------------------------

  // Eén primaire knop: nog niet (of een oude versie) verstuurd → versturen, anders omzetten
  const sendIsNext = sendState.state !== QUOTE_SEND_STATE.SENT;
  const moreMenu = {
    items: [
      urls.webshopQuoteEdit
        ? {
            key: "webshop",
            icon: <ShopOutlined />,
            label: (
              <a href={urls.webshopQuoteEdit(quote.id)} target="_blank" rel="noopener noreferrer">
                {t("quote.actions.edit_in_webshop")}
              </a>
            ),
          }
        : null,
      { key: "cancel", icon: <StopOutlined />, danger: true, label: t("quote.actions.cancel"), onClick: cancelQuote },
    ].filter(Boolean),
  };

  return (
    <div className="purchase-detail-page">
      {contextHolder}
      <PageHeader
        title={`${t("quote.quote")} ${quote.quoteNumber || ""}`}
        crumbLabel={quote.quoteNumber || "…"}
        subtitle={[customerLabel(customer), formatDate(quote.createdAt), version > 1 ? `v${version}` : null]
          .filter(Boolean)
          .join(" · ")}
        extra={
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.quotesList)}>
              {t("back")}
            </Button>
            {canManage && open && !editing ? (
              <>
                <Button icon={<EditOutlined />} onClick={startEditing}>
                  {t("edit")}
                </Button>
                <Button
                  type={sendIsNext ? "primary" : "default"}
                  icon={<MailOutlined />}
                  onClick={() => setSendOpen(true)}
                >
                  {t("quote.actions.send")}
                </Button>
                <Button
                  type={sendIsNext ? "default" : "primary"}
                  icon={<SwapOutlined />}
                  onClick={() => setConvertOpen(true)}
                >
                  {t("quote.actions.convert")}
                </Button>
                <Dropdown menu={moreMenu} trigger={["click"]}>
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </>
            ) : null}
          </>
        }
      />

      {displayStatus === QUOTE_DISPLAY_STATUS.CONVERTED ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 20 }}
          message={t("quote.converted_notice", {
            order: quote.orderId || "-",
            date: formatDateTime(quote.convertedAt),
            name: quote.convertedBy || "-",
          })}
          action={
            convertedOrder ? (
              <Button size="small" onClick={() => setOrderOpen(true)}>
                {t("quote.open_order")}
              </Button>
            ) : null
          }
        />
      ) : null}
      {displayStatus === QUOTE_DISPLAY_STATUS.CANCELLED ? (
        <Alert type="info" showIcon style={{ marginBottom: 20 }} message={t("quote.cancelled_notice")} />
      ) : null}
      {displayStatus === QUOTE_DISPLAY_STATUS.EXPIRED ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
          message={t("quote.expired_notice", { date: formatDate(quote.validUntil) })}
        />
      ) : null}
      {open && sendState.state === QUOTE_SEND_STATE.FAILED ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
          message={t("quote.send_failed_notice", { date: formatDateTime(sendState.at), error: sendState.error || "-" })}
        />
      ) : null}
      {open && sendState.state === QUOTE_SEND_STATE.CHANGED ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
          message={t("quote.changed_notice", { sentVersion: sendState.version, version })}
        />
      ) : null}
      {editing ? <Alert type="info" showIcon style={{ marginBottom: 20 }} message={t("quote.edit.explainer")} /> : null}

      <DetailLayout
        aside={
          <>
            <SectionCard title={t("quote.section.quote")}>
              <KeyValueList items={quoteItems} />
            </SectionCard>
            <SectionCard title={t("quote.section.amounts")}>
              <KeyValueList items={amountItems} labelWidth={130} />
            </SectionCard>
            <SectionCard title={t("customer.key")}>
              <KeyValueList items={customerItems} />
            </SectionCard>
          </>
        }
      >
        <SectionCard
          title={t("order.products")}
          meta={t("quote.line_meta", {
            count: lines.length,
            pieces: lines.reduce((sum, line) => sum + (line.quantity || 0), 0),
          })}
        >
          <Table
            columns={lineColumns}
            // Regels hebben geen eigen id; bewerken gaat op positie, dus de positie is de sleutel
            dataSource={lines.map((line, index) => ({ ...line, rowKey: index }))}
            rowKey="rowKey"
            pagination={false}
            size="small"
            scroll={{ x: 860 }}
          />
        </SectionCard>

        {editing || quote.orderNotes ? (
          <SectionCard title={t("quote.notes")} padded>
            {editing ? (
              <Input.TextArea
                rows={3}
                maxLength={220}
                showCount
                value={draft.orderNotes}
                onChange={(event) => setDraft((current) => ({ ...current, orderNotes: event.target.value }))}
              />
            ) : (
              <div style={{ whiteSpace: "pre-wrap" }}>{quote.orderNotes}</div>
            )}
          </SectionCard>
        ) : null}

        <SectionCard title={t("quote.section.history")}>
          <ActivityTimeline items={history} emptyText={t("quote.history.empty")} />
        </SectionCard>
      </DetailLayout>

      {editing ? (
        <StickyActionBar
          note={isDirty ? t("quote.edit.dirty_note", { version: draft.baseVersion + 1 }) : t("quote.edit.clean_note")}
        >
          <Button onClick={() => setDraft(null)} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button type="primary" onClick={save} loading={isSaving} disabled={!isDirty}>
            {t("save")}
          </Button>
        </StickyActionBar>
      ) : null}

      <SendQuoteModal
        quote={quote}
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSent={(result) => messageApi.success(t("quote.send.sent", { email: result.to }))}
      />
      <ConvertQuoteModal
        quote={quote}
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        onConverted={(result) => notifyConverted(messageApi, t, result)}
      />
      <OrderDrawer order={convertedOrder} open={orderOpen} closeDrawer={() => setOrderOpen(false)} />
    </div>
  );
};

export default QuoteDetail;
